import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/api';
import { PageLoader } from './Spinner';
import { getErrorMessage } from '../utils/errors';
import { getPostAuthPath } from '../utils/redirect';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    
    const processAuth = async () => {
      // Get authorization code from query params (Google OAuth redirect)
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        const errorDescription = searchParams.get('error_description') || '';
        toast.error(`Google authentication failed: ${errorDescription || error}`);
        navigate('/login');
        return;
      }

      if (!code) {
        toast.error('No authorization code found. Please try again.');
        navigate('/login');
        return;
      }

      hasProcessed.current = true;

      try {
        // Exchange authorization code for JWT
        // Send the redirect URI we used so backend can match it
        const redirectUri = `${window.location.origin}/auth/google`;
        const response = await axios.post(`${API_URL}/auth/google`, {
          code: code,
          redirect_uri: redirectUri
        });

        const { token, user } = response.data;
        
        if (!token || !user) {
          throw new Error('Invalid response from server');
        }
        
        // Update auth context properly
        setAuth(token, user);
        
        toast.success('Successfully signed in with Google!');
        
        // Use navigate instead of window.location.href for proper React routing
        navigate(getPostAuthPath(null, user), { replace: true });
        
      } catch (error) {
        toast.error(getErrorMessage(error, 'Google sign-in failed. Please try again.'));
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setAuth]);

  return <PageLoader label="Signing you in with Google" />;
}
